import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getRequest, pathname } from "@react/helpers/fetch"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import Input  from "@react/shared/ui/Input"

const SendEmailModal = ({ isOpen, onClose, sendUrl, defaults = {} }) => {
  const [subject,   setSubject]   = useState("")
  const [recipient, setRecipient] = useState("")
  const [cc,        setCc]        = useState("")
  const [message,   setMessage]   = useState("")
  const [onlineSig, setOnlineSig] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [templateId, setTemplateId] = useState("")

  const templates = defaults.templates || []

  useEffect(() => {
    if (!isOpen) return
    setSubject(defaults.subject || "")
    setRecipient(defaults.recipient || "")
    setCc("")
    setMessage(defaults.message || "")
    setOnlineSig(!!defaults.online_signature)
    setSubmitting(false)
    setTemplateId("")
  }, [isOpen, defaults])

  // The empty option holds the message built by default for the quote.
  const pickTemplate = value => {
    setTemplateId(value)
    const template = templates.find(t => String(t.id) === String(value))
    setSubject((template ? template.subject : defaults.subject) || "")
    setMessage((template ? template.message : defaults.message) || "")
  }

  const canSubmit = subject.trim() && recipient.trim() && !submitting

  const handleSubmit = () => {
    if (!canSubmit) return
    setSubmitting(true)
    const url = pathname({
      path:   sendUrl,
      params: {
        subject,
        recipient,
        recipients:        cc,
        message,
        online_signature:  onlineSig ? "true" : "false",
      },
    })
    getRequest(url)
      .then(response => {
        if (response?.errors) {
          flashAlert(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors))
          return
        }
        flashNotice(I18n.t("quotes.builder.meta.email_sent"))
        onClose()
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))
      .finally(() => setSubmitting(false))
  }

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      size    = "lg"
      title   = {I18n.t("quotes.builder.meta.send_email_title")}
      footer  = {
        <>
          <Button variant="ghost" onClick={onClose}>{I18n.t("button.cancel")}</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            <FontAwesomeIcon icon={submitting ? "spinner" : "paper-plane"} spin={submitting} />
            {I18n.t("quotes.bigcard.send")}
          </Button>
        </>
      }
    >
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700">{I18n.t("quotes.bigcard.recipient")}</label>
        <Input
          type        = "email"
          value       = {recipient}
          onChange    = {e => setRecipient(e.target.value)}
          placeholder = "destinataire@exemple.com"
        />
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700">{I18n.t("quotes.bigcard.sendCopy")}</label>
        <Input
          type        = "text"
          value       = {cc}
          onChange    = {e => setCc(e.target.value)}
          placeholder = "copy1@mail.com, copy2@mail.com"
        />
      </div>

      {templates.length > 0 && (
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">{I18n.t("email_templates.picker_label")}</label>
          <select
            value     = {templateId}
            onChange  = {e => pickTemplate(e.target.value)}
            className = "w-full px-3 py-2 rounded-md border border-slate-200 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          >
            <option value="">{I18n.t("email_templates.picker_default")}</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>{template.title}</option>
            ))}
          </select>
        </div>
      )}

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700">{I18n.t("quotes.bigcard.subject")}</label>
        <Input value={subject} onChange={e => setSubject(e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-slate-700">{I18n.t("quotes.bigcard.message")}</label>
        <textarea
          value     = {message}
          onChange  = {e => setMessage(e.target.value)}
          rows      = {8}
          className = "w-full px-3 py-2 rounded-md border border-slate-200 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input
          type     = "checkbox"
          checked  = {onlineSig}
          onChange = {e => setOnlineSig(e.target.checked)}
        />
        {I18n.t("quotes.email.online_signature")}
      </label>
    </Modal>
  )
}

export default SendEmailModal
